/**
 * Feature-local client for the public signup API (#1496 / #3017).
 *
 * Deliberately not the app's `api` client: these endpoints are unauthenticated
 * and must never carry a session, a refresh attempt or a global 401 handler.
 * Contract: docs/operations/self-service-signup-contract.md.
 *
 * Every call resolves — never rejects — to `{ ok: true, data }` or
 * `{ ok: false, status, code }`. The UI picks its text from `code`, not from
 * the HTTP status and never from the server's `error` string.
 */

import { z } from 'zod';
import type { SignupRequestPayload } from './signupForm';
import type { SignupCheckoutSession } from './signupSession';

const SIGNUP_BASE = '/api/v1/signup';

export interface ApiFailure {
  readonly ok: false;
  /** HTTP status; `0` when no response arrived. */
  readonly status: number;
  /** Server `code`, or one of our own: `network_error`, `rate_limited`, `invalid_response`, `generic`. */
  readonly code: string;
}

export type ApiResult<T> = { readonly ok: true; readonly data: T } | ApiFailure;

export const SIGNUP_STAGES = [
  'awaiting_checkout',
  'awaiting_payment',
  'provisioning',
  'provisioning_failed',
  'ready',
  'failed',
  'expired',
] as const;
export type SignupStage = (typeof SIGNUP_STAGES)[number];

export const SIGNUP_ISSUES = [
  'payment_failed',
  'checkout_expired',
  'slug_conflict',
  'tenant_rejected',
  'signup_not_fulfillable',
] as const;
export type SignupIssue = (typeof SIGNUP_ISSUES)[number];

/** Codes that have their own text under `errors.*` in the `signup` namespace. */
const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set([
  'validation_failed',
  'invalid_business_id',
  'company_not_found',
  'registry_unavailable',
  'slug_reserved',
  'slug_invalid',
  'slug_taken',
  'duplicate_registration',
  'verification_email_failed',
  'invalid_token',
  'invalid_capability',
  'signup_not_payable',
  'checkout_in_progress',
  'checkout_provider_error',
  'checkout_provider_unavailable',
  'checkout_not_configured',
  'checkout_invalid_url',
  'rate_limited',
  'network_error',
  'invalid_response',
]);

/** i18n key (signup namespace) for a failure; unknown codes read as a generic error. */
export function errorKeyFor(code: string): string {
  return KNOWN_ERROR_CODES.has(code) ? `errors.${code}` : 'errors.generic';
}

const createdSchema = z.object({ id: z.string(), status: z.string() });
const verifiedSchema = z.object({
  id: z.string().min(1),
  checkoutToken: z.string().min(1),
  checkoutTokenExpiresAt: z.string().optional(),
});
const checkoutSchema = z.object({
  id: z.string(),
  status: z.string(),
  checkoutUrl: z.string().min(1),
  reused: z.boolean().optional(),
});
const progressSchema = z.object({
  id: z.string(),
  status: z.string(),
  stage: z.string(),
  issue: z.string().nullable().optional(),
  slug: z.string().nullable().optional(),
});

export interface SignupProgress {
  readonly stage: SignupStage | 'unknown';
  readonly issue: SignupIssue | null;
  readonly slug: string | null;
}

export interface CheckoutHandoff {
  readonly checkoutUrl: string;
}

function isStage(value: string): value is SignupStage {
  return (SIGNUP_STAGES as readonly string[]).includes(value);
}

function isIssue(value: string): value is SignupIssue {
  return (SIGNUP_ISSUES as readonly string[]).includes(value);
}

async function postJson<T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
): Promise<ApiResult<T>> {
  let response: Response;
  try {
    response = await fetch(`${SIGNUP_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      // Public endpoints: no cookies, and the capability travels in the body only.
      credentials: 'omit',
      cache: 'no-store',
    });
  } catch {
    return { ok: false, status: 0, code: 'network_error' };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    if (response.status === 429) return { ok: false, status: 429, code: 'rate_limited' };
    const code =
      typeof payload === 'object' &&
      payload !== null &&
      typeof (payload as { code?: unknown }).code === 'string'
        ? (payload as { code: string }).code
        : 'generic';
    return { ok: false, status: response.status, code };
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) return { ok: false, status: response.status, code: 'invalid_response' };
  return { ok: true, data: parsed.data };
}

/** `POST /signup` — creates the lead and sends the verification mail. */
export async function submitSignup(
  payload: SignupRequestPayload,
): Promise<ApiResult<{ id: string; status: string }>> {
  return postJson('', payload, createdSchema);
}

/** `POST /signup/verify` — consumes the mail token; the capability is shown once. */
export async function verifySignupToken(token: string): Promise<ApiResult<SignupCheckoutSession>> {
  const result = await postJson('/verify', { token }, verifiedSchema);
  if (!result.ok) return result;
  return { ok: true, data: { id: result.data.id, checkoutToken: result.data.checkoutToken } };
}

/** `POST /signup/checkout` — the client sends nothing but the id and capability. */
export async function startSignupCheckout(
  session: SignupCheckoutSession,
): Promise<ApiResult<CheckoutHandoff>> {
  const result = await postJson(
    '/checkout',
    { id: session.id, checkoutToken: session.checkoutToken },
    checkoutSchema,
  );
  if (!result.ok) return result;
  return { ok: true, data: { checkoutUrl: result.data.checkoutUrl } };
}

/** `POST /signup/checkout/status` — read-only; the only proof of payment or provisioning. */
export async function fetchSignupProgress(
  session: SignupCheckoutSession,
): Promise<ApiResult<SignupProgress>> {
  const result = await postJson(
    '/checkout/status',
    { id: session.id, checkoutToken: session.checkoutToken },
    progressSchema,
  );
  if (!result.ok) return result;
  const { stage, issue, slug } = result.data;
  return {
    ok: true,
    data: {
      stage: isStage(stage) ? stage : 'unknown',
      issue: issue && isIssue(issue) ? issue : null,
      slug: slug ?? null,
    },
  };
}
